import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@material-ui/core";
import CrmMemberAvatar from "../org/CrmMemberAvatar";
import { formatContactPhone } from "../crmFormat";
import type { CrmHandlerView } from "./crmHandlerFace";

/**
 * THE SOFT GUARDRAIL — "a colleague is already running this lead. Still want to?"
 *
 * Fires before the three OUTBOUND actions when the agency has designated somebody other
 * than the viewer: sending a document for signature, creating a meeting or task, and
 * (as an inline notice rather than this dialog) adding people to a WhatsApp campaign.
 *
 * ── Every wording choice here is deliberate ────────────────────────────────────
 *
 * THE TITLE NAMES THE SITUATION, NOT THE USER'S MISTAKE. "מתווך אחר מטפל בלקוח" — not
 * "שים לב", not "פעולה חורגת". Nobody has done anything wrong yet.
 *
 * THE HANDLER'S FACE IS IN THE BODY. A name is a string; a face is a person you have to
 * think about coordinating with. That is the entire mechanism of a soft guardrail — the
 * dialog is not an obstacle, it is an introduction.
 *
 * "אפשר להמשיך" IS STATED EXPLICITLY, IN THE BODY. The user should learn on the first
 * encounter that this never blocks. A guardrail people believe is a block is a guardrail
 * people learn to route around.
 *
 * THE CONTINUE BUTTON IS THE PRIMARY, CONTAINED, AND `color="primary"` — not `error`.
 * Making it scary turns a soft guardrail into a hard one in practice, which is the one
 * thing the product decision ruled out.
 */
type Props = {
  open: boolean;
  handler: CrmHandlerView;
  contactId: string;
  displayName?: string | null;
  onCancel: () => void;
  onContinue: () => void;
};

export default function CrmHandlerGuardDialog({ open, handler, contactId, displayName, onCancel, onContinue }: Props) {
  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>מתווך אחר מטפל בלקוח</DialogTitle>

      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
          <CrmMemberAvatar name={handler.name} avatarUrl={handler.avatarUrl} size={40} />
          <Typography variant="body2">
            <b>{handler.name}</b>
            {" מטפל/ת ב־"}
            {displayName || (
              // An LTR island via the `dir` ATTRIBUTE — `sx={{ direction }}` is mirrored by
              // stylis-plugin-rtl into its opposite.
              <Typography component="span" variant="body2" dir="ltr" sx={{ display: "inline-block" }}>
                {formatContactPhone(contactId)}
              </Typography>
            )}
            {" מטעם המשרד."}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          אפשר להמשיך — כדאי לתאם איתו/ה קודם, כדי שלא תפנו שניכם ללקוח.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          ביטול
        </Button>
        <Button onClick={onContinue} variant="contained" color="primary" autoFocus>
          המשך בכל זאת
        </Button>
      </DialogActions>
    </Dialog>
  );
}
